import {
  DEFAULT_DELIVERY_TIME_THRESHOLDS,
  DEFAULT_EM_THRESHOLDS,
  DEFAULT_INTRAPARTUM_THRESHOLDS,
  DEFAULT_TIME_LOG_FIELD_LABELS,
  DeliveryTimeThreshold,
  EmThreshold,
  IntrapartumThreshold,
  SETTINGS,
  TIME_LOG_CONFIG,
  TimeLogConfig,
  TimeLogFieldKey,
  UpdateCallback,
} from '@hb/shared'
import { DocumentReference } from 'firebase/firestore'
import { useCallback, useEffect, useRef } from 'react'
import { useDocument } from '../../../../backend/db/hooks'
import { useUpdateDoc } from '../../../../hooks'

export interface UseTimeLogConfigAdmin {
  data: TimeLogConfig | null
  loading: boolean
  ref: DocumentReference<TimeLogConfig> | null
  handleUpdateFieldLabel: (key: TimeLogFieldKey, label: string) => Promise<UpdateCallback>
  handleResetFieldLabel: (key: TimeLogFieldKey) => Promise<UpdateCallback>
  handleAddThreshold: (threshold: EmThreshold) => Promise<UpdateCallback>
  handleUpdateThreshold: (index: number, threshold: EmThreshold) => Promise<UpdateCallback>
  handleDeleteThreshold: (index: number) => Promise<UpdateCallback>
  // §4.3: Intrapartum threshold management
  handleAddIntrapartumThreshold: (threshold: IntrapartumThreshold) => Promise<UpdateCallback>
  handleUpdateIntrapartumThreshold: (
    index: number,
    threshold: IntrapartumThreshold,
  ) => Promise<UpdateCallback>
  handleDeleteIntrapartumThreshold: (index: number) => Promise<UpdateCallback>
  // §5.2: Delivery time threshold management
  handleAddDeliveryThreshold: (threshold: DeliveryTimeThreshold) => Promise<UpdateCallback>
  handleUpdateDeliveryThreshold: (
    index: number,
    threshold: DeliveryTimeThreshold,
  ) => Promise<UpdateCallback>
  handleDeleteDeliveryThreshold: (index: number) => Promise<UpdateCallback>
}

export const useTimeLogConfigAdmin = (): UseTimeLogConfigAdmin => {
  const { data, loading, ref } = useDocument<TimeLogConfig>(SETTINGS, TIME_LOG_CONFIG)
  const update = useUpdateDoc('timelogconfig')

  const seeded = useRef(false)
  useEffect(() => {
    if (!loading && !data && ref && !seeded.current) {
      seeded.current = true
      update(ref, 'emThresholds', DEFAULT_EM_THRESHOLDS)
    }
  }, [loading, data, ref, update])

  const handleUpdateFieldLabel = useCallback(
    async (key: TimeLogFieldKey, label: string): Promise<UpdateCallback> =>
      update(ref, `fieldLabels.${key}`, label),
    [ref, update],
  )

  const handleResetFieldLabel = useCallback(
    async (key: TimeLogFieldKey): Promise<UpdateCallback> =>
      update(ref, `fieldLabels.${key}`, DEFAULT_TIME_LOG_FIELD_LABELS[key]),
    [ref, update],
  )

  const buildSortedThresholds = (thresholds: EmThreshold[]) =>
    [...thresholds].sort((a, b) => a.minMinutes - b.minMinutes)

  const handleAddThreshold = useCallback(
    async (threshold: EmThreshold): Promise<UpdateCallback> => {
      const current = data?.emThresholds ?? DEFAULT_EM_THRESHOLDS
      return update(ref, 'emThresholds', buildSortedThresholds([...current, threshold]))
    },
    [ref, data, update],
  )

  const handleUpdateThreshold = useCallback(
    async (index: number, threshold: EmThreshold): Promise<UpdateCallback> => {
      const current = data?.emThresholds ?? DEFAULT_EM_THRESHOLDS
      const next = current.map((t, i) => (i === index ? threshold : t))
      return update(ref, 'emThresholds', buildSortedThresholds(next))
    },
    [ref, data, update],
  )

  const handleDeleteThreshold = useCallback(
    async (index: number): Promise<UpdateCallback> => {
      const current = data?.emThresholds ?? DEFAULT_EM_THRESHOLDS
      return update(
        ref,
        'emThresholds',
        current.filter((_, i) => i !== index),
      )
    },
    [ref, data, update],
  )

  // §4.3: Intrapartum threshold handlers (same pattern as antepartum E&M thresholds)
  const handleAddIntrapartumThreshold = useCallback(
    async (threshold: IntrapartumThreshold): Promise<UpdateCallback> => {
      const current = data?.intrapartumThresholds ?? DEFAULT_INTRAPARTUM_THRESHOLDS
      return update(
        ref,
        'intrapartumThresholds',
        [...current, threshold].sort((a, b) => a.minMinutes - b.minMinutes),
      )
    },
    [ref, data, update],
  )

  const handleUpdateIntrapartumThreshold = useCallback(
    async (index: number, threshold: IntrapartumThreshold): Promise<UpdateCallback> => {
      const current = data?.intrapartumThresholds ?? DEFAULT_INTRAPARTUM_THRESHOLDS
      return update(
        ref,
        'intrapartumThresholds',
        current
          .map((t, i) => (i === index ? threshold : t))
          .sort((a, b) => a.minMinutes - b.minMinutes),
      )
    },
    [ref, data, update],
  )

  const handleDeleteIntrapartumThreshold = useCallback(
    async (index: number): Promise<UpdateCallback> => {
      const current = data?.intrapartumThresholds ?? DEFAULT_INTRAPARTUM_THRESHOLDS
      return update(
        ref,
        'intrapartumThresholds',
        current.filter((_, i) => i !== index),
      )
    },
    [ref, data, update],
  )

  // §5.2: Delivery time threshold handlers (same pattern as intrapartum thresholds)
  const handleAddDeliveryThreshold = useCallback(
    async (threshold: DeliveryTimeThreshold): Promise<UpdateCallback> => {
      const current = data?.deliveryTimeThresholds ?? DEFAULT_DELIVERY_TIME_THRESHOLDS
      return update(
        ref,
        'deliveryTimeThresholds',
        [...current, threshold].sort((a, b) => a.minMinutes - b.minMinutes),
      )
    },
    [ref, data, update],
  )

  const handleUpdateDeliveryThreshold = useCallback(
    async (index: number, threshold: DeliveryTimeThreshold): Promise<UpdateCallback> => {
      const current = data?.deliveryTimeThresholds ?? DEFAULT_DELIVERY_TIME_THRESHOLDS
      return update(
        ref,
        'deliveryTimeThresholds',
        current
          .map((t, i) => (i === index ? threshold : t))
          .sort((a, b) => a.minMinutes - b.minMinutes),
      )
    },
    [ref, data, update],
  )

  const handleDeleteDeliveryThreshold = useCallback(
    async (index: number): Promise<UpdateCallback> => {
      const current = data?.deliveryTimeThresholds ?? DEFAULT_DELIVERY_TIME_THRESHOLDS
      return update(
        ref,
        'deliveryTimeThresholds',
        current.filter((_, i) => i !== index),
      )
    },
    [ref, data, update],
  )

  return {
    data,
    loading,
    ref,
    handleUpdateFieldLabel,
    handleResetFieldLabel,
    handleAddThreshold,
    handleUpdateThreshold,
    handleDeleteThreshold,
    handleAddIntrapartumThreshold,
    handleUpdateIntrapartumThreshold,
    handleDeleteIntrapartumThreshold,
    handleAddDeliveryThreshold,
    handleUpdateDeliveryThreshold,
    handleDeleteDeliveryThreshold,
  }
}
